import { EyeIcon, PencilIcon } from 'lucide-react'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { toast } from 'sonner'

import { type GrapeAPIErrorType } from 'components/hooks/GrapeAPIError'
import { useCreateQuoteAllowances } from 'components/hooks/quotes/useCreateQuoteAllowances'
import { useStateFilter } from 'components/hooks/quotes/useStateFilter'
import { OutdatedConfigModal } from 'components/quote_explorer/OutdatedConfigModal'
import { QuotingEmployeeProvider } from 'components/quoting/employees/QuotingEmployeeContext'
import { QuotePricingProvider, useQuotePricing } from 'components/quoting/quotes/pricing/QuotePricingProvider'
import LoadingSpinnerCentered from 'components/reusable/LoadingSpinnerCentered'
import { Banner, Button, TooltipProvider } from 'slab'
import { getYearFromDateString } from 'utils'
import { calculateEmployeeMonthlyAllowanceForEmployeeAndDependents, calculateMonthlyEmployeeOutOfPocketContribution, planIsAffordable } from 'utils/quote_calculations'
import {
  useGetQuotingQuoteResultsQuoteId,
  useGetQuotingQuoteResultsQuoteIdResults,
  usePutQuotingPricesQuoteId as useUpdateQuotePricing,
} from 'utils/web_api'
import { type QuotingEmployee, type QuotingQuoteResult } from 'utils/web_api.schemas'
import { useWindowLocation } from 'utils/windowLocation'

import ActionButtons from './ActionButtons'
import { EditableQuoteTitle } from './EditableQuoteTitle'
import EmployeeDetailsSheet from './EmployeeDetailsSheet'
import Map from './Map'
import { QuoteConfigurationSliders } from './QuoteConfigurationSliders'
import { QuoteProvider } from './QuoteProvider'
import SidePanelContent from './SidePanelContent'
import { UnsavedChangesModal } from './UnsavedChangesModal'
import { type MetalTier, type QuoteAllowance } from '../prospects/types'
import { EmployeesTable } from '../quoting/employees/table/EmployeesTable'

const initialMetalTierFromQuoteAllowance = (allowance?: QuoteAllowance) => {
  return allowance?.benchmark?.split('_')[2]
}

type QuoteEditorProps = {
  acceptQuoteUrl?: string
  allowances?: QuoteAllowance[]
  canEditQuote: boolean
  canOnboardFromQuote: boolean
  canShareQuote: boolean
  defaultMetalTier?: string
  defaultCoveragePercentile?: number
  defaultEmployeeContribution?: number
  defaultSpouseContribution?: number
  defaultChildContribution?: number
  mapboxAccessToken: string
  prospectAssociatedWithEmployer?: boolean
  workflowType: WorkflowType
  outdatedConfig?: boolean
  prospectId: string
  prospectName: string
  quoteId: string
  initialMode?: QuoteExplorerMode
  canSendEditorInvites: boolean
  canSendOnboardInvites: boolean
}

export type WorkflowType = 'broker' | 'sales' | 'public' | 'renewal'

export enum QuoteExplorerMode {
  explore = 'explore',
  edit = 'edit',
}

const DEFAULT_EMPLOYER_EMPLOYEE_CONTRIBUTION = 100
const DEFAULT_EMPLOYER_SPOUSE_CONTRIBUTION = 100
const DEFAULT_EMPLOYER_CHILD_CONTRIBUTION = 100

export const QuoteExplorer = ({
  acceptQuoteUrl,
  allowances,
  canEditQuote,
  canOnboardFromQuote,
  canShareQuote,
  defaultMetalTier,
  defaultCoveragePercentile,
  defaultEmployeeContribution,
  defaultSpouseContribution,
  defaultChildContribution,
  mapboxAccessToken,
  prospectId,
  prospectAssociatedWithEmployer,
  workflowType,
  outdatedConfig = false,
  prospectName,
  quoteId,
  initialMode = QuoteExplorerMode.explore,
  canSendEditorInvites,
  canSendOnboardInvites,
}: QuoteEditorProps) => {
  const { assign, reload } = useWindowLocation()

  const { data: quote, error: fetchQuoteError, isLoading: isQuoteLoading, refetch: refetchQuote } = useGetQuotingQuoteResultsQuoteId(quoteId)
  const [selectedMetalTier, setSelectedMetalTier] = useState<string | null>(initialMetalTierFromQuoteAllowance(allowances?.[0]) ?? defaultMetalTier ?? null)
  const [selectedCoveragePercentile, setSelectedCoveragePercentile] = useState<number | null>(allowances?.[0]?.coverage_percentile ?? defaultCoveragePercentile ?? null)
  const [employerEmployeeContribution, setEmployerEmployeeContribution] = useState<number>((allowances?.[0]?.employee_contribution_percentage ?? defaultEmployeeContribution ?? DEFAULT_EMPLOYER_EMPLOYEE_CONTRIBUTION) / 100)
  const [employerSpouseContribution, setEmployerSpouseContribution] = useState<number>((allowances?.[0]?.spouse_contribution_percentage ?? defaultSpouseContribution ?? DEFAULT_EMPLOYER_SPOUSE_CONTRIBUTION) / 100)
  const [employerChildContribution, setEmployerChildContribution] = useState<number>((allowances?.[0]?.dependent_contribution_percentage ?? defaultChildContribution ?? DEFAULT_EMPLOYER_CHILD_CONTRIBUTION) / 100)

  const [lastSavedSelectedMetalTier, setLastSavedSelectedMetalTier] = useState<string | null>(initialMetalTierFromQuoteAllowance(allowances?.[0]) ?? selectedMetalTier)
  const [lastSavedSelectedCoveragePercentile, setLastSavedSelectedCoveragePercentile] = useState<number | null>(allowances?.[0]?.coverage_percentile ?? selectedCoveragePercentile)
  const [lastSavedEmployerEmployeeContribution, setLastSavedEmployerEmployeeContribution] = useState<number>((allowances?.[0]?.employee_contribution_percentage ?? employerEmployeeContribution) / 100)
  const [lastSavedEmployerSpouseContribution, setLastSavedEmployerSpouseContribution] = useState<number>((allowances?.[0]?.spouse_contribution_percentage ?? employerSpouseContribution) / 100)
  const [lastSavedEmployerChildContribution, setLastSavedEmployerChildContribution] = useState<number>((allowances?.[0]?.dependent_contribution_percentage ?? employerChildContribution) / 100)

  const [mode, setMode] = useState<QuoteExplorerMode>(initialMode)
  const [openUnsavedChangesModal, setOpenUnsavedChangesModal] = useState<boolean>(false)

  const { filteredStates, toggleState, setStates } = useStateFilter()
  const [quoteIsUnsaved, setQuoteIsUnsaved] = useState<boolean>(allowances?.length === 0)
  const isExploreMode = mode === QuoteExplorerMode.explore

  const { quotePricing, setQuoteType } = useQuotePricing()

  const quoteYear = useMemo(() => {
    return quote?.coverage_start_date ? getYearFromDateString(quote.coverage_start_date) : new Date().getFullYear() + 1
  }, [quote?.coverage_start_date])

  const handleNavigate = (url: string) => {
    assign(url)
  }

  useEffect(() => {
    if (quote?.status === 'processing') {
      const interval = setInterval(() => {
        refetchQuote()
      }, 1000)
      return () => clearInterval(interval)
    }
  }, [quote?.status, refetchQuote])

  useEffect(() => {
    if (quote) {
      if (!selectedMetalTier && quote.metal_tiers?.[0]) {
        setSelectedMetalTier(quote.metal_tiers[0])
      }
      if (!selectedCoveragePercentile && quote.coverage_percentiles?.[0]) {
        setSelectedCoveragePercentile(quote.coverage_percentiles[0])
      }

      if (!quote.pricing_configured) {
        setQuoteIsUnsaved(true)
      }

      setQuoteType(quote.quote_type)
    }
  }, [quote])

  const { data: quoteResults, error: fetchQuoteResultsError, isLoading } = useGetQuotingQuoteResultsQuoteIdResults(quoteId, {
    metal_tier: selectedMetalTier! as MetalTier,
    coverage_percentile: selectedCoveragePercentile!,
  }, {
    query: {
      refetchOnWindowFocus: false,
      enabled: !!selectedMetalTier && !!selectedCoveragePercentile,
    },
  })

  const createQuoteAllowancesMutation = useCreateQuoteAllowances({
    prospectId,
    quoteId,
    removeExisting: true,
  })

  const updateQuotePricingMutation = useUpdateQuotePricing()

  const eligibleEmployees = useMemo(() => {
    return quoteResults?.filter((result) => result.eligible) ?? []
  }, [quoteResults])

  const numEmployees = useMemo(() => {
    return eligibleEmployees?.length ?? 0
  }, [eligibleEmployees])

  const numUniqueStates = useMemo(() => {
    return new Set(eligibleEmployees?.map((result) => result.rating_area_code.substring(0, 2))).size ?? 0
  }, [eligibleEmployees])

  const hasUnsavedChanges = useMemo(() => {
    return (
      quoteIsUnsaved ||
      selectedMetalTier !== lastSavedSelectedMetalTier ||
      selectedCoveragePercentile !== lastSavedSelectedCoveragePercentile ||
      employerEmployeeContribution !== lastSavedEmployerEmployeeContribution ||
      employerSpouseContribution !== lastSavedEmployerSpouseContribution ||
      employerChildContribution !== lastSavedEmployerChildContribution
    )
  }, [
    quoteIsUnsaved, selectedMetalTier, selectedCoveragePercentile, employerEmployeeContribution, employerSpouseContribution,
    employerChildContribution, lastSavedSelectedMetalTier, lastSavedSelectedCoveragePercentile,
    lastSavedEmployerEmployeeContribution, lastSavedEmployerSpouseContribution, lastSavedEmployerChildContribution,
  ])

  const quoteResultsByEmployeeId = useMemo(() => {
    return quoteResults?.reduce((acc, result) => {
      acc[result.id] = result
      return acc
    }, {} as Record<string, QuotingQuoteResult>)
  }, [quoteResults])

  const onClickSaveQuote = () => {
    if (!quote?.pricing_configured && quotePricing) {
      // Ensure that the quote pricing is set prior to sending a quote
      updateQuotePricingMutation.mutate({
        quoteId,
        data: quotePricing,
      })
    }

    createQuoteAllowancesMutation.mutate([{
      dynamic_config: {
        coverage_percentile: selectedCoveragePercentile!,
        benchmark: `on_thatch_${selectedMetalTier}`,
        employee_contribution_percentage: employerEmployeeContribution * 100,
        spouse_contribution_percentage: employerSpouseContribution * 100,
        dependent_contribution_percentage: employerChildContribution * 100,
      },
    }], {
      onSuccess: () => {
        setLastSavedSelectedMetalTier(selectedMetalTier)
        setLastSavedSelectedCoveragePercentile(selectedCoveragePercentile)
        setLastSavedEmployerEmployeeContribution(employerEmployeeContribution)
        setLastSavedEmployerSpouseContribution(employerSpouseContribution)
        setLastSavedEmployerChildContribution(employerChildContribution)
        setQuoteIsUnsaved(false)
        toast.success('Quote saved!')
        setMode(QuoteExplorerMode.explore)
      },
      onError: () => {
        toast.error('Failed to save quote')
      }
    })
  }

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isExploreMode && (e.metaKey || e.ctrlKey) && e.key === 's') {
        e.preventDefault()
        if (!createQuoteAllowancesMutation.isPending && hasUnsavedChanges) {
          onClickSaveQuote()
        }
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [createQuoteAllowancesMutation.isPending, hasUnsavedChanges, onClickSaveQuote])

  const discardChanges = () => {
    setSelectedMetalTier(lastSavedSelectedMetalTier)
    setSelectedCoveragePercentile(lastSavedSelectedCoveragePercentile)
    setEmployerEmployeeContribution(lastSavedEmployerEmployeeContribution)
    setEmployerSpouseContribution(lastSavedEmployerSpouseContribution)
    setEmployerChildContribution(lastSavedEmployerChildContribution)
    toast.success('Changes discarded')
  }

  const monthlyAllowanceForEmployee = useCallback((employee: QuotingEmployee) => {
    const quoteResult = quoteResultsByEmployeeId?.[employee.id]
    if (!quoteResult) {
      return 0
    }

    return calculateEmployeeMonthlyAllowanceForEmployeeAndDependents(quoteResult, {
      employerEmployeeContribution,
      employerSpouseContribution,
      employerChildContribution,
    })
  }, [quoteResultsByEmployeeId, employerEmployeeContribution, employerSpouseContribution, employerChildContribution])

  const monthlyEmployeeOutOfPocketContribution = useCallback((employee: QuotingEmployee) => {
    const quoteResult = quoteResultsByEmployeeId?.[employee.id]
    if (!quoteResult) {
      return 0
    }

    return calculateMonthlyEmployeeOutOfPocketContribution(quoteResult, {
      employerEmployeeContribution,
      employerSpouseContribution,
      employerChildContribution,
    })
  }, [quoteResultsByEmployeeId, employerEmployeeContribution, employerSpouseContribution, employerChildContribution])

  const isAllowanceAffordable = useCallback((employee: QuotingEmployee) => {
    const quoteResult = quoteResultsByEmployeeId?.[employee.id]
    if (!quoteResult) {
      return false
    }

    return planIsAffordable(quoteResult, {
      employerEmployeeContribution,
    })
  }, [quoteResultsByEmployeeId, employerEmployeeContribution])

  const isEmployeeInProjectedState = useCallback((employee: QuotingEmployee) => {
    if (!quote?.preliminary) return false

    const stateCode = employee.rating_area_code.substring(0, 2)
    return quote?.projected_data_state_codes?.includes(stateCode) ?? false
  }, [quote?.preliminary, quote?.projected_data_state_codes])

  const minAffordableAllowanceForEmployee = useCallback((employee: QuotingEmployee) => {
    const quoteResult = quoteResultsByEmployeeId?.[employee.id]
    if (!quoteResult) {
      return
    }

    return quoteResult.min_employer_contribution
  }, [quoteResultsByEmployeeId])

  const medicalPlanForEmployee = useCallback((employee: QuotingEmployee) => {
    const quoteResult = quoteResultsByEmployeeId?.[employee.id]
    if (!quoteResult) {
      return
    }

    return quoteResult.medical_plan
  }, [quoteResultsByEmployeeId])

  if (fetchQuoteError || fetchQuoteResultsError) {
    const apiError = (fetchQuoteError || fetchQuoteResultsError) as GrapeAPIErrorType
    return (
      <div className='h-full w-full flex items-center justify-center text-gray-500 text-md'>
        <div className='text-center'>
          Error loading quote details
          {apiError.status === 404 && (
            <div className='mt-4'>
              <Button onClick={() => reload()} variant='primary'>
                Your session may have timed out. Reload?
              </Button>
            </div>
          )}
        </div>
      </div>
    )
  }

  if (quote && quote.status === 'failed') {
    return (
      <div className='flex flex-col items-center justify-center h-screen'>
        <Banner type='critical' title='Failed to process quote'>
          {workflowType === 'sales'
            ? (
                quote.quote_errors?.map((error, index) => (
                  <p key={`quote-error-${index}`}>{error}</p>
                )))
            : <p>There was an error processing your quote. Please try again or contact support for further assistance.</p>}
        </Banner>
      </div>
    )
  }

  if (quote && quote.status !== 'processed') {
    return (
      <div className='flex flex-col items-center justify-center h-screen'>
        <div className='text-lg'>
          Processing your quote <LoadingSpinnerCentered />
        </div>
      </div>
    )
  }

  return (
    <QuoteProvider quote={quote}>
      <QuotingEmployeeProvider
        employees={eligibleEmployees ?? []}
        monthlyAllowanceForEmployee={monthlyAllowanceForEmployee}
        monthlyEmployeeOutOfPocketContribution={monthlyEmployeeOutOfPocketContribution}
        isAllowanceAffordable={isAllowanceAffordable}
        minAffordableAllowanceForEmployee={minAffordableAllowanceForEmployee}
        medicalPlanForEmployee={medicalPlanForEmployee}
        isEmployeeInProjectedState={isEmployeeInProjectedState}
      >
        <UnsavedChangesModal
          open={openUnsavedChangesModal}
          onClose={() => setOpenUnsavedChangesModal(false)}
          onDismissChanges={() => {
            setOpenUnsavedChangesModal(false)
            discardChanges()
            setMode(QuoteExplorerMode.explore)
          }}
          onSaveChanges={() => {
            setOpenUnsavedChangesModal(false)
            onClickSaveQuote()
            setMode(QuoteExplorerMode.explore)
          }}
        />
        <div className='flex flex-col h-full w-full bg-white'>
          <div className='flex h-full'>
            <div className='w-64 h-full p-5 overflow-y-auto flex flex-col justify-between space-y-5 border-r border-gray-200'>
              <div>
                <QuoteConfigurationSliders
                  mode={mode}
                  availableCoveragePercentiles={(quote?.coverage_percentiles ?? []).map(cp => cp.toString())}
                  availableMetalTiers={quote?.metal_tiers ?? []}
                  selectedMetalTier={selectedMetalTier ?? ''}
                  setSelectedMetalTier={setSelectedMetalTier}
                  employerEmployeeContribution={employerEmployeeContribution}
                  employerSpouseContribution={employerSpouseContribution}
                  employerChildContribution={employerChildContribution}
                  setEmployerEmployeeContribution={setEmployerEmployeeContribution}
                  setEmployerSpouseContribution={setEmployerSpouseContribution}
                  setEmployerChildContribution={setEmployerChildContribution}
                  selectedCoveragePercentile={selectedCoveragePercentile ?? 50}
                  setSelectedCoveragePercentile={setSelectedCoveragePercentile}
                  coverageStartDate={quote?.coverage_start_date ?? null}
                  prospectId={prospectId}
                  quoteId={quoteId}
                  workflowType={workflowType}
                  quoteType={quote?.quote_type}
                />
              </div>
              <a
                className='text-xs underline decoration-dotted transition-colors duration-200 text-gray-600 group-hover:text-gray-900'
                href='mailto:sales@thatch.com'
              >
                Need help? Talk to sales
              </a>
            </div>
            <div className='flex-1 p-5 h-full overflow-y-auto'>
              <div className='flex justify-between'>
                <EditableQuoteTitle
                  quote={quote}
                  prospectId={prospectId}
                  quoteId={quoteId}
                  prospectName={prospectName}
                  mode={mode}
                  isLoading={isQuoteLoading}
                />
                {canEditQuote && (
                  <Button
                    variant='secondary'
                    size='small'
                    onClick={() => {
                      if (hasUnsavedChanges && !isExploreMode) {
                        setOpenUnsavedChangesModal(true)
                      } else {
                        setMode(prev => prev === QuoteExplorerMode.explore ? QuoteExplorerMode.edit : QuoteExplorerMode.explore)
                      }
                    }}
                  >
                    <div className='flex items-center gap-1'>
                      {isExploreMode && <><PencilIcon className='w-4 h-4' /> Edit</>}
                      {!isExploreMode && <><EyeIcon className='w-4 h-4' /> Explore</>}
                    </div>
                  </Button>
                )}
              </div>
              <h2 className='text-sm text-gray-500 mt-2'>
                {eligibleEmployees ? numEmployees : <span className='bg-gray-200 h-3 w-4 rounded animate-pulse' />} employees in {eligibleEmployees ? numUniqueStates : <span className='bg-gray-200 h-3 w-4 rounded animate-pulse' />} state{numUniqueStates === 1 ? '' : 's'}
              </h2>
              <div className='my-4'>
                <Map
                  mapboxAccessToken={mapboxAccessToken}
                  isLoading={isLoading}
                  quoteResults={eligibleEmployees}
                  employerEmployeeContribution={employerEmployeeContribution}
                  employerSpouseContribution={employerSpouseContribution}
                  employerChildContribution={employerChildContribution}
                  filteredStates={filteredStates}
                  onStateClick={toggleState}
                  onStatesSelected={states => {
                    if (filteredStates.sort().join(' ') === states.sort().join(' ')) {
                      setStates([])
                    } else {
                      setStates(states)
                    }
                  }}
                />
              </div>
              <EmployeesTable
                isLoading={isLoading}
                employees={eligibleEmployees ?? []}
                filteredStates={filteredStates}
                onStateFilterChange={toggleState}
              />
            </div>
            <div className='w-[416px] h-full bg-gray-100 border-l border-gray-200'>
              <div className='flex flex-col h-full'>
                <div className='p-5 flex flex-col gap-2 flex-1 overflow-y-auto min-h-0'>
                  <SidePanelContent
                    employees={eligibleEmployees ?? []}
                    employerEmployeeContribution={employerEmployeeContribution}
                    employerSpouseContribution={employerSpouseContribution}
                    employerChildContribution={employerChildContribution}
                    isLoading={isLoading}
                    mode={mode}
                  />
                </div>
                <div className='p-5 flex flex-col gap-2 flex-shrink-0 shadow-default shadow-gray-800/20 border-t border-gray-200'>
                  {isExploreMode && (
                    <p className='text-xs text-gray-500 mb-2'>
                      These estimates may vary based on the plan choices your employees make and actual participation numbers.
                    </p>
                  )}
                  <ActionButtons
                    mode={mode}
                    saveButtonDisabled={createQuoteAllowancesMutation.isPending || !hasUnsavedChanges || isQuoteLoading}
                    onClickSaveQuote={onClickSaveQuote}
                    hasUnsavedChanges={hasUnsavedChanges}
                    discardChanges={discardChanges}
                    downloadLink={quote?.download_link ?? null}
                    quoteId={quoteId}
                    prospectName={prospectName}
                    acceptQuoteUrl={acceptQuoteUrl ?? null}
                    prospectAssociatedWithEmployer={!!prospectAssociatedWithEmployer}
                    canOnboardFromQuote={canOnboardFromQuote}
                    canShareQuote={canShareQuote}
                    canSendEditorInvites={canSendEditorInvites}
                    canSendOnboardInvites={canSendOnboardInvites}
                    canEditQuote={canEditQuote}
                    workflowType={workflowType}
                    quoteType={quote?.quote_type}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
        <EmployeeDetailsSheet />
      </QuotingEmployeeProvider>
      <OutdatedConfigModal
        quoteId={quoteId}
        prospectId={prospectId}
        quoteYear={quoteYear}
        onNavigate={handleNavigate}
        isOpen={outdatedConfig && !quote?.has_flat_allowances}
        workflowType={workflowType}
      />
    </QuoteProvider>
  )
}

export default (props: QuoteEditorProps) => {
  return (
    <TooltipProvider>
      <QuotePricingProvider quoteId={props.quoteId} workflowType={props.workflowType}>
        <QuoteExplorer {...props} />
      </QuotePricingProvider>
    </TooltipProvider>
  )
}
